// src/utils/share.js
// Handles album/story sharing for web + native.
// - Small payloads: compressed into URL hash using LZ-String
// - Large payloads: encrypted and uploaded to Firebase Storage, with a tiny hash
//   that contains only a share id + encryption key (in the URL fragment).

import LZString from 'lz-string';
import { db, storage } from '../firebaseClient';
import { ref, uploadBytes, getBytes } from 'firebase/storage';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { analyzeAndPrint, estimateOptimalSize } from './payloadAnalyzer';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// maximum size we allow for inline-compressed payloads in the URL hash
const MAX_INLINE_HASH_CHARS = 2000;
const MAX_INLINE_JSON_BYTES = 400 * 1024;

const STORAGE_PREFIX = 'S-'; // hash format: S-<shareId>.<key>.<iv>

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function randomId(len = 16) {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  const array = new Uint8Array(len);
  window.crypto.getRandomValues(array);
  for (let i = 0; i < len; i++) {
    out += chars[array[i] % chars.length];
  }
  return out;
}

function arrayBufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = window.btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64urlToArrayBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = window.atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function isCryptoAvailable() {
  return typeof window !== 'undefined' &&
    !!window.crypto &&
    !!window.crypto.subtle;
}

// ---------------------------------------------------------------------------
// Inline encode/decode (old behavior)
// ---------------------------------------------------------------------------

export function encodeAlbumPayload(payload) {
  try {
    if (!payload || typeof payload !== 'object') {
      throw new Error('Invalid payload: must be an object');
    }
    const json = JSON.stringify(payload);
    const compressed = LZString.compressToEncodedURIComponent(json);
    return compressed;
  } catch (err) {
    console.error('[encodeAlbumPayload] Failed:', err);
    return null;
  }
}

export function decodeAlbumPayload(hash) {
  if (!hash) return null;
  if (hash.startsWith(STORAGE_PREFIX)) {
    // new storage style; handled by loadStoredAlbumFromHash
    return null;
  }
  try {
    const json = LZString.decompressFromEncodedURIComponent(hash);
    if (!json) return null;
    return JSON.parse(json);
  } catch (err) {
    console.error('[decodeAlbumPayload] Failed:', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Encrypted storage helpers
// ---------------------------------------------------------------------------

async function generateAesKey() {
  const key = await window.crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
  const raw = await window.crypto.subtle.exportKey('raw', key);
  return { key, raw };
}

async function encryptBytes(key, dataBytes) {
  const iv = window.crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV
  const ciphertext = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    dataBytes
  );
  return { iv, ciphertext };
}

async function uploadEncryptedPayload(jsonBytes, mode = 'story') {
  console.log('[uploadEncryptedPayload] 🚀 Starting Firebase upload...');
  
  if (!db || !storage) {
    console.error('[uploadEncryptedPayload] ❌ Firebase not initialized!', { db: !!db, storage: !!storage });
    throw new Error('Firebase db/storage not initialized. Check firebaseClient.js & env vars.');
  }

  console.log('[uploadEncryptedPayload] ✅ Firebase initialized');

  const bytes = jsonBytes instanceof Uint8Array ? jsonBytes : new Uint8Array(jsonBytes);
  console.log('[uploadEncryptedPayload] 📦 Payload size:', (bytes.byteLength / 1024).toFixed(2), 'KB');
  
  console.log('[uploadEncryptedPayload] 🔐 Generating encryption key...');
  const { key, raw } = await generateAesKey();
  console.log('[uploadEncryptedPayload] ✅ Key generated');
  
  console.log('[uploadEncryptedPayload] 🔒 Encrypting payload...');
  const { iv, ciphertext } = await encryptBytes(key, bytes);
  console.log('[uploadEncryptedPayload] ✅ Encryption complete, encrypted size:', (ciphertext.byteLength / 1024).toFixed(2), 'KB');

  const shareId = randomId(16);
  const path = `shares/${shareId}/payload.enc`;
  console.log('[uploadEncryptedPayload] 📤 Uploading to Firebase Storage:', path);

  try {
    const fileRef = ref(storage, path);
    await uploadBytes(fileRef, new Uint8Array(ciphertext));
    console.log('[uploadEncryptedPayload] ✅ File uploaded successfully');
  } catch (uploadErr) {
    console.error('[uploadEncryptedPayload] ❌ Upload failed:', uploadErr);
    console.error('[uploadEncryptedPayload] Error details:', {
      message: uploadErr.message,
      code: uploadErr.code,
      name: uploadErr.name,
      serverResponse: uploadErr.serverResponse
    });
    throw new Error(`Firebase Storage upload failed: ${uploadErr.message}`);
  }

  console.log('[uploadEncryptedPayload] 💾 Creating Firestore metadata document...');
  try {
    const metaRef = doc(db, 'shares', shareId);
    await setDoc(metaRef, {
      createdAt: serverTimestamp(),
      mode,
      sizeBytes: bytes.byteLength,
      version: 1,
    });
    console.log('[uploadEncryptedPayload] ✅ Metadata saved');
  } catch (firestoreErr) {
    console.error('[uploadEncryptedPayload] ❌ Firestore metadata save failed:', firestoreErr);
    console.warn('[uploadEncryptedPayload] ⚠️  File uploaded but metadata failed - continuing anyway');
  }

  const keyB64 = arrayBufferToBase64url(raw);
  const ivB64 = arrayBufferToBase64url(iv.buffer);
  const hash = `${STORAGE_PREFIX}${shareId}.${keyB64}.${ivB64}`;

  console.log('[uploadEncryptedPayload] 🎉 Upload complete!', {
    shareId,
    hashLength: hash.length,
    hash: hash.substring(0, 50) + '...'
  });

  return { hash, shareId };
}

export function isStoredShareHash(hash) {
  return !!hash && hash.startsWith(STORAGE_PREFIX);
}

export async function loadStoredAlbumFromHash(hash) {
  console.log("[loadStoredAlbumFromHash] 🔐 Starting to load stored album...");
  console.log("[loadStoredAlbumFromHash] Input hash:", hash?.substring(0, 50) + "...");
  
  if (!isStoredShareHash(hash)) {
    console.log("[loadStoredAlbumFromHash] ❌ Not a stored share hash");
    return null;
  }
  
  if (!isCryptoAvailable()) {
    console.error("[loadStoredAlbumFromHash] ❌ Web Crypto not available");
    throw new Error('Web Crypto not available');
  }
  
  if (!db || !storage) {
    console.error("[loadStoredAlbumFromHash] ❌ Firebase not initialized", { db, storage });
    throw new Error('Firebase not initialized');
  }

  console.log("[loadStoredAlbumFromHash] ✅ Firebase initialized successfully");

  const withoutPrefix = hash.slice(STORAGE_PREFIX.length);
  const parts = withoutPrefix.split('.');
  console.log("[loadStoredAlbumFromHash] Hash parts count:", parts.length);
  
  if (parts.length < 3) {
    console.error("[loadStoredAlbumFromHash] ❌ Invalid hash format, expected 3+ parts, got:", parts.length);
    throw new Error('Invalid storage hash format');
  }
  
  const [shareId, keyB64, ivB64] = parts;
  console.log("[loadStoredAlbumFromHash] Parsed:", {
    shareId: shareId?.substring(0, 8) + "...",
    keyLength: keyB64?.length,
    ivLength: ivB64?.length
  });

  try {
    console.log("[loadStoredAlbumFromHash] 🔑 Decoding key and IV...");
    const keyRaw = base64urlToArrayBuffer(keyB64);
    const ivBuf = base64urlToArrayBuffer(ivB64);
    const iv = new Uint8Array(ivBuf);
    console.log("[loadStoredAlbumFromHash] ✅ Key and IV decoded");

    console.log("[loadStoredAlbumFromHash] 🔓 Importing AES key...");
    const key = await window.crypto.subtle.importKey(
      'raw',
      keyRaw,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );
    console.log("[loadStoredAlbumFromHash] ✅ AES key imported");

    const path = `shares/${shareId}/payload.enc`;
    console.log("[loadStoredAlbumFromHash] 📥 Fetching from Firebase Storage:", path);
    
    const fileRef = ref(storage, path);
    const encryptedBytes = await getBytes(fileRef);
    console.log("[loadStoredAlbumFromHash] ✅ Downloaded encrypted data:", encryptedBytes.byteLength, "bytes");

    console.log("[loadStoredAlbumFromHash] 🔓 Decrypting...");
    const plain = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encryptedBytes
    );
    console.log("[loadStoredAlbumFromHash] ✅ Decryption successful:", plain.byteLength, "bytes");

    const json = new TextDecoder().decode(plain);
    console.log("[loadStoredAlbumFromHash] 📄 JSON length:", json.length, "characters");
    
    const parsed = JSON.parse(json);
    console.log("[loadStoredAlbumFromHash] ✅ Successfully parsed JSON, has pages:", parsed?.pages?.length || 0);
    
    return parsed;
  } catch (err) {
    console.error("[loadStoredAlbumFromHash] ❌ Error during load:", err);
    console.error("[loadStoredAlbumFromHash] Error details:", {
      message: err.message,
      code: err.code,
      name: err.name,
      stack: err.stack
    });
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Decide inline vs storage
// ---------------------------------------------------------------------------

async function buildShareHashForPayload(payload, mode = 'story') {
  console.log('\n🔍 === SHARE DIAGNOSTIC === 🔍');
  console.log('[buildShareHashForPayload] Starting share process...');
  
  const json = JSON.stringify(payload);
  const encoder = new TextEncoder();
  const jsonBytes = encoder.encode(json);
  const compressed = LZString.compressToEncodedURIComponent(json);

  const sizeBytes = jsonBytes.byteLength;
  const hashChars = compressed.length;
  const sizeKB = (sizeBytes / 1024).toFixed(2);
  const compressedKB = (hashChars / 1024).toFixed(2);

  console.log('📊 Payload Analysis:', {
    pages: payload.pages?.length || 0,
    photos: payload.photos?.length || 0,
    jsonLength: json.length,
    sizeBytes,
    sizeKB: `${sizeKB} KB`,
    hashChars,
    compressedKB: `${compressedKB} KB`,
    maxInlineChars: MAX_INLINE_HASH_CHARS,
    maxInlineBytes: MAX_INLINE_JSON_BYTES / 1024 + ' KB'
  });

  // Decision logic with detailed logging
  if (hashChars <= MAX_INLINE_HASH_CHARS && sizeBytes <= MAX_INLINE_JSON_BYTES) {
    console.log('✅ DECISION: Using INLINE hash (within limits)');
    console.log(`   Hash chars: ${hashChars} <= ${MAX_INLINE_HASH_CHARS} ✓`);
    console.log(`   Size bytes: ${sizeBytes} <= ${MAX_INLINE_JSON_BYTES} ✓`);
    return { hash: compressed, strategy: 'inline' };
  }

  console.log('⚠️  Payload exceeds inline limits:');
  if (hashChars > MAX_INLINE_HASH_CHARS) {
    console.log(`   ❌ Hash chars: ${hashChars} > ${MAX_INLINE_HASH_CHARS} (${(hashChars - MAX_INLINE_HASH_CHARS)} over)`);
  }
  if (sizeBytes > MAX_INLINE_JSON_BYTES) {
    console.log(`   ❌ Size bytes: ${sizeKB} KB > ${(MAX_INLINE_JSON_BYTES / 1024).toFixed(2)} KB (${((sizeBytes - MAX_INLINE_JSON_BYTES) / 1024).toFixed(2)} KB over)`);
  }

  // Automatically analyze why payload is large
  console.log('\n🔬 Running automatic payload analysis...');
  analyzeAndPrint(payload);
  
  // Show optimization potential
  const optimal = estimateOptimalSize(payload);
  if (optimal) {
    console.log('💡 Optimization Potential:');
    console.log(`   Current: ${optimal.original.kb} KB`);
    console.log(`   Optimal: ${optimal.optimal.kb} KB`);
    console.log(`   Could reduce by: ${optimal.reduction.kb} KB (${optimal.reduction.percentage})`);
  }

  if (!isCryptoAvailable()) {
    console.warn('❌ FALLBACK: No Web Crypto available, using inline despite size');
    console.warn('   This will create a very long URL that may not work!');
    return { hash: compressed, strategy: 'inline-forced' };
  }

  console.log('🔐 DECISION: Using encrypted Firebase Storage');
  console.log('   Attempting upload...');
  
  try {
    const { hash, shareId } = await uploadEncryptedPayload(jsonBytes, mode);
    console.log('✅ Firebase upload successful!', { shareId, hashLength: hash.length });
    return { hash, strategy: 'storage', shareId };
  } catch (err) {
    console.error('❌ Firebase upload failed:', err);
    console.error('   Error details:', {
      message: err.message,
      code: err.code,
      name: err.name
    });
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Public sharing helpers
// ---------------------------------------------------------------------------

// Return the share hash & strategy without opening windows or using navigator.share
export async function getShareHashForPayload(payload, mode = 'story') {
  // Reuses the same decision logic (inline vs Firebase)
  return await buildShareHashForPayload(payload, mode);
}


async function shareLink(link, title) {
  if (navigator.share) {
    try {
      await navigator.share({ title, url: link });
      return { success: true, link };
    } catch (err) {
      if (err && err.name === 'AbortError') {
        return { success: false, cancelled: true };
      }
      console.warn('[shareLink] navigator.share failed, falling back to clipboard', err);
    }
  }

  try {
    await navigator.clipboard.writeText(link);
    return { success: true, link, copied: true };
  } catch (err) {
    console.error('[shareLink] Failed to copy link', err);
    return { success: false, link };
  }
}




/**
 * Main share helper used by AlbumFromPast (Share button)
 */
export async function shareAsStory(payload, title = 'My Album') {
  try {
    const { hash } = await buildShareHashForPayload(payload, 'story');
    const link = `${window.location.origin}/story#${hash}`;
    return await shareLink(link, title);
  } catch (err) {
    console.error('[shareAsStory] Failed to share story', err);
    return { success: false, error: err };
  }
}

/**
 * Preview helper – uses the *same* logic as shareAsStory, but instead of
 * navigator.share it simply opens the link in a new tab.
 */
export async function previewStoryLocally(payload) {
  try {
    const { hash } = await buildShareHashForPayload(payload, 'story-preview');
    const link = `${window.location.origin}/story#${hash}`;
    window.open(link, '_blank', 'noopener,noreferrer');
    return link;
  } catch (err) {
    console.error('[previewStoryLocally] Failed to open preview', err);
    throw err;
  }
}

// Optional flipbook share variant, left here if you ever want it
export async function shareAsFlipbook(payload, title = 'My Album') {
  try {
    const { hash } = await buildShareHashForPayload(payload, 'flipbook');
    const link = `${window.location.origin}/story#${hash}&view=flipbook`;
    return await shareLink(link, title);
  } catch (err) {
    console.error('[shareAsFlipbook] Failed to share flipbook', err);
    return { success: false, error: err };
  }
}

// ---------------------------------------------------------------------------
// Duration helpers (used by AlbumFromPast diagnostics)
// ---------------------------------------------------------------------------

export function calculateTotalDuration(pages = []) {
  if (!Array.isArray(pages)) return 0;
  return pages.reduce((sum, p) => {
    const d = typeof p.durationMs === 'number' ? p.durationMs : 0;
    return sum + d;
  }, 0);
}

export function formatStoryDuration(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}